import React from 'react'
import { m } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import { PressContent } from '../../../types/content.types'
import { useTheme } from '../../../context/ThemeContext'

interface PressProps {
    content: PressContent
}

/**
 * 언론보도(press) 섹션 — 한돈 전문지·언론에 소개된 흑돈1931 기사 카드.
 * 각 카드는 원문 기사(외부 링크)를 새 탭으로 연다.
 * 한국어에서만 노출된다(RestaurantPage에서 language 게이팅).
 */
const Press: React.FC<PressProps> = ({ content }) => {
    const { theme } = useTheme()

    const items = content.items || []
    if (items.length === 0) return null

    return (
        <section id="press" className={`restaurant-press theme-${theme.mode}`}>
            <div className="restaurant-press-container">
                <m.div
                    className="restaurant-press-header"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="restaurant-press-label">{content.label}</span>
                    <div className="restaurant-press-divider" />
                    <h2 className="restaurant-press-title">{content.title}</h2>
                    {content.description && (
                        <p className="restaurant-press-desc">{content.description}</p>
                    )}
                </m.div>

                <div className="restaurant-press-list">
                    {items.map((item, i) => (
                        <m.a
                            key={item.url}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="restaurant-press-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            whileHover={{ y: -4 }}
                            aria-label={`${item.outlet} 기사 원문 보기 (새 창)`}
                        >
                            <div className="restaurant-press-card-top">
                                <span className="restaurant-press-outlet">{item.outlet}</span>
                                {item.badge && <span className="restaurant-press-badge">{item.badge}</span>}
                            </div>
                            <h3 className="restaurant-press-card-title">{item.title}</h3>
                            <p className="restaurant-press-excerpt">{item.excerpt}</p>
                            <div className="restaurant-press-card-foot">
                                <span className="restaurant-press-date">{item.date}</span>
                                <span className="restaurant-press-more">
                                    기사 보기 <ArrowUpRight size={14} />
                                </span>
                            </div>
                        </m.a>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default React.memo(Press)
